import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createInvestment, deleteInvestment, getPrivate, moveInvestment, updateInvestment } from '../../data/private';
import { getHoldings } from '../../data/portfolio';
import PortfolioTabs from '../portfolio/PortfolioTabs';
import PortfolioHeader from '../portfolio/PortfolioHeader';
import PrivateTable from '../portfolio/PrivateTable';
import SectionSummary from '../portfolio/SectionSummary';
import useSummaryInputs from '../portfolio/useSummaryInputs';
import { partsIn, summaryParts } from '../portfolio/sections';
import { Skeleton } from '../portfolio/Loading';
import { summarize } from '../portfolio/privateMaths';
import { formatPct } from '../portfolio/timeframes';
import { useT } from '../../i18n';

// Venture positions, DAO memberships, real estate and Medora Fund 1.
//
// Two sections: what is still held, and what has been exited. Only the ACTIVE
// marks count towards the Total Portfolio — an exit's proceeds are already cash
// in some other account. Medora is an IBKR account: its mark is the account's
// live value from the holdings snapshot, and only its cost is kept here.

const LOAD_STATE = { LOADING: 'loading', READY: 'ready', ERROR: 'error' };

/** A catalogue key for a failed request — never the server's English text. */
const errorKey = (err, fallback) => {
    if (err?.status === 401) return 'private.errors.session';
    if (err?.status === 0) return 'private.errors.offline';
    if (err?.status === 400) return 'private.errors.invalid';
    return fallback;
};

function Summary({ title, stats, exited }) {
    const { t, tn, fmt } = useT();
    const money = (v) => (v === null ? '—' : `$${fmt.number(v, 0)}`);
    const tone = stats.pnl === null || stats.pnl === 0 ? '' : stats.pnl < 0 ? ' is-negative' : ' is-positive';
    const signedMoney = (v) => (v === null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}$${fmt.number(Math.abs(v), 0)}`);

    return (
        <div className="perf private__summary">
            <h2 className="notes__h">{title}</h2>
            <div className="perf__head">
                <div>
                    <span className="perf__label">{t(exited ? 'private.summary.returned' : 'private.summary.mark')}</span>
                    <span className="perf__value">{money(exited ? stats.distributed : stats.mark)}</span>
                </div>
                <div>
                    <span className="perf__label">{t('private.summary.invested')}</span>
                    <span className="perf__value">{money(stats.invested)}</span>
                    <span className="perf__note">
                        {tn('private.summary.counts', stats.count, { rounds: stats.rounds })}
                    </span>
                </div>
                <div>
                    <span className="perf__label">{t(exited ? 'private.summary.realised' : 'private.summary.pnl')}</span>
                    <span className={`perf__value${tone}`}>
                        {signedMoney(stats.pnl)}
                        {formatPct(stats.pnlPct, 1) && (
                            <span className="perf__row_pct">{formatPct(stats.pnlPct, 1)}</span>
                        )}
                    </span>
                    {/* Held investments can have paid something back already;
                        the P/L counts it, so say how much of it that is. */}
                    {!exited && stats.distributed > 0 && (
                        <span className="perf__note">{t('private.summary.withDistributed', { amount: money(stats.distributed) })}</span>
                    )}
                </div>
                <div>
                    <span className="perf__label">{t('private.summary.multiple')}</span>
                    <span className="perf__value">
                        {stats.multiple === null ? '—' : `${fmt.number(stats.multiple, 2)}x`}
                    </span>
                </div>
            </div>
            {stats.unmarked > 0 && (
                <p className="perf__period">{tn(exited ? 'private.summary.unreturned' : 'private.summary.unmarked', stats.unmarked)}</p>
            )}
        </div>
    );
}

export default function PrivateInvestments() {
    const { t } = useT();
    const [state, setState] = useState(LOAD_STATE.LOADING);
    const [investments, setInvestments] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [positions, setPositions] = useState([]);
    const [baseCurrency, setBaseCurrency] = useState('USD');
    const [windowKey, setWindowKey] = useState('all');
    const { inputs, setInput } = useSummaryInputs();
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(false);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const load = useCallback(async () => {
        const result = await getPrivate();
        if (!mounted.current) return;
        setInvestments(result?.investments ?? []);
        setState(LOAD_STATE.READY);
    }, []);

    useEffect(() => {
        getHoldings()
            .then((result) => {
                if (!mounted.current) return;
                setAccounts(result?.accounts ?? []);
                setPositions(result?.positions ?? []);
                if (result?.baseCurrency) setBaseCurrency(result.baseCurrency);
            })
            .catch(() => {});
        load().catch((err) => {
            if (!mounted.current) return;
            setNotice(errorKey(err, 'private.errors.load'));
            setState(LOAD_STATE.ERROR);
        });
    }, [load]);

    /** Runs a write, then reloads: the list is org-wide, so the server's copy
     *  is the one to show, not a local patch of it. */
    const write = async (action, failKey) => {
        setBusy(true);
        setNotice(null);
        try {
            await action();
            await load();
            return true;
        } catch (err) {
            setNotice(errorKey(err, failKey));
            return false;
        } finally {
            if (mounted.current) setBusy(false);
        }
    };

    const save = (id, value) => write(
        () => (id ? updateInvestment(id, value) : createInvestment(value)),
        'private.errors.save',
    );
    const move = (id, status) => write(() => moveInvestment(id, status), 'private.errors.move');
    const remove = (id) => write(() => deleteInvestment(id), 'private.errors.delete');

    // A linked investment's mark is its account's live value, and its
    // holdings are that account's positions — listed nowhere else.
    const shown = useMemo(() => investments.map((i) => {
        if (!i.linkedAccountId) return i;
        const account = accounts.find((a) => a.accountId === i.linkedAccountId);
        return {
            ...i,
            currentMark: account?.totalValue === undefined || account?.totalValue === null ? null : Number(account.totalValue),
            holdings: positions.filter((p) => p.accountId === i.linkedAccountId),
        };
    }), [investments, accounts, positions]);
    const active = useMemo(() => shown.filter((i) => i.status === 'active'), [shown]);
    const exited = useMemo(() => shown.filter((i) => i.status === 'exited'), [shown]);
    const parts = useMemo(
        () => partsIn(summaryParts(accounts, { inputs, privateList: investments }), 'private'),
        [accounts, inputs, investments],
    );

    const section = (status, list, titleKey) => (
        <section className={`private__section is-${status}`}>
            {status === 'exited'
                ? <Summary title={t(titleKey)} stats={summarize(list)} exited />
                : <h2 className="notes__h">{t(titleKey)}</h2>}
            <PrivateTable
                investments={list}
                status={status}
                onSave={save}
                onMove={move}
                onDelete={remove}
                busy={busy}
                baseCurrency={baseCurrency}
            />
        </section>
    );

    return (
        <section className="holdings__sec">
            <div className="wrapper">
                {/* No refresh control: like Notes and Transactions, this is
                    fetched fresh on every visit. */}
                <PortfolioHeader title={t('private.title')} />

                <PortfolioTabs />

                {state === LOAD_STATE.LOADING && <Skeleton rows={10} />}

                {state === LOAD_STATE.ERROR && (
                    <p className="holdings__error" role="alert">{t(notice ?? 'private.errors.load')}</p>
                )}

                {state === LOAD_STATE.READY && (
                    <>
                        {notice && <p className="holdings__error" role="alert">{t(notice)}</p>}
                        <SectionSummary
                            parts={parts}
                            windowKey={windowKey}
                            onWindow={setWindowKey}
                            inputs={inputs}
                            onInput={setInput}
                            showParts
                        />
                        {section('active', active, 'private.active')}
                        {section('exited', exited, 'private.exits')}
                    </>
                )}
            </div>
        </section>
    );
}
